import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import extractApiError from '../../utils/apiError';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { logError } from '../../utils/logError';
import { formatTutarTR } from '../../utils/para';
import SearchableSelect from '../../components/SearchableSelect';

// PR-C (PC5b) — Satış Faturası kesme formu.
// Müşteri seç → SEVK_EDILDI (faturalanabilir) irsaliyeler checklist → POST /api/satis-faturalari
// (irsaliye_ids[]). Backend doğrudan keser (ayrı taslak adımı YOK), kalemleri snapshot'lar.
// İrsaliye liste ucu toplam DÖNDÜRMEZ → seçilen her irsaliyenin detayını çekip brut toplarız.
// POST body: {cari_id, satan_firma_id, tarih, vade_tarihi, notlar?, irsaliye_ids[]}

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const labelStyle = { display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#555' };
const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box',
};

const fmtTarih = (val) => {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString('tr-TR');
};

const todayStr = () => new Date().toISOString().split('T')[0];
const addDaysStr = (gun) => {
  const d = new Date();
  d.setDate(d.getDate() + (gun || 0));
  return d.toISOString().split('T')[0];
};

const SatisFaturasiOlusturPage = () => {
  const { user } = useAuth();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_SATIS, user.effectivePermissions);
  const toast = useToast();
  const navigate = useNavigate();

  const [cariler, setCariler] = useState([]);
  const [firmalar, setFirmalar] = useState([]);

  const [cariId, setCariId] = useState('');
  const [satanFirmaId, setSatanFirmaId] = useState('');
  const [tarih, setTarih] = useState(todayStr());
  const [vadeTarihi, setVadeTarihi] = useState(addDaysStr(30));
  const [notlar, setNotlar] = useState('');

  const [irsaliyeler, setIrsaliyeler] = useState([]); // SEVK_EDILDI + faturasız
  const [irsLoading, setIrsLoading] = useState(false);
  const [secili, setSecili] = useState({}); // irsaliye_id → true
  const [brutMap, setBrutMap] = useState({}); // irsaliye_id → toplam brut (detaydan)
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetch(`${API}/api/cari?tip=MUSTERI`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setCariler(Array.isArray(data) ? data : []))
      .catch(() => setCariler([]));
    fetch(`${API}/api/firmalar`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setFirmalar(list);
        // Satan firma default TORUNA_KASAP (kod ile bul → id'sini seç)
        const tk = list.find((f) => f.kod === 'TORUNA_KASAP');
        if (tk) setSatanFirmaId(String(tk.id));
      })
      .catch(() => setFirmalar([]));
  }, [token]);

  // Seçilen müşterinin faturalanabilir irsaliyelerini çek + tek irsaliyenin brut toplamını detaydan al.
  const fetchIrsaliyeler = useCallback(async (selectedCariId) => {
    setIrsaliyeler([]);
    setSecili({});
    setBrutMap({});
    if (!selectedCariId) return;
    setIrsLoading(true);
    try {
      const params = new URLSearchParams({ durum: 'SEVK_EDILDI', cari_id: String(selectedCariId) });
      const res = await fetch(`${API}/api/satis-irsaliyeleri?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        toast.error(extractApiError(data.detail));
        return;
      }
      // SEVK_EDILDI + henüz faturalanmamış (satis_fatura_id null) — güvenlik için istemci tarafında da süz.
      const list = (Array.isArray(data) ? data : []).filter((ir) => !ir.satis_fatura_id);
      setIrsaliyeler(list);

      // Her irsaliyenin brut toplamını detaydan çek (liste ucu toplam döndürmez).
      list.forEach((ir) => {
        fetch(`${API}/api/satis-irsaliyeleri/${ir.id}`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => {
            if (!d || !Array.isArray(d.kalemler)) return;
            const brut = d.kalemler.reduce((s, k) => s + (Number(k.satir_brut) || 0), 0);
            setBrutMap((prev) => ({ ...prev, [ir.id]: brut }));
          })
          .catch(() => {});
      });
    } catch (err) {
      logError('Faturalanabilir irsaliye yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setIrsLoading(false);
    }
  }, [token, toast]);

  const handleCariChange = (val) => {
    setCariId(val);
    // Müşterinin vade_gun değeri varsa vade tarihini ona göre öner.
    const c = cariler.find((x) => String(x.id) === String(val));
    if (c && c.vade_gun != null) setVadeTarihi(addDaysStr(c.vade_gun));
    fetchIrsaliyeler(val);
  };

  const toggleSecim = (id) => {
    setSecili((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const seciliIdler = irsaliyeler.filter((ir) => secili[ir.id]).map((ir) => ir.id);
  const seciliBrutToplam = seciliIdler.reduce((s, id) => s + (brutMap[id] || 0), 0);
  const toplamHesaplandi = seciliIdler.every((id) => brutMap[id] != null);

  const handleKaydet = async () => {
    if (!cariId) { toast.error('Müşteri seçiniz'); return; }
    if (!satanFirmaId) { toast.error('Satan firma seçiniz'); return; }
    if (!tarih) { toast.error('Fatura tarihi giriniz'); return; }
    if (!vadeTarihi) { toast.error('Vade tarihi giriniz'); return; }
    if (seciliIdler.length === 0) { toast.error('En az bir irsaliye seçiniz'); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/satis-faturalari`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          cari_id: parseInt(cariId, 10),
          satan_firma_id: parseInt(satanFirmaId, 10),
          tarih,
          vade_tarihi: vadeTarihi,
          notlar: notlar || null,
          irsaliye_ids: seciliIdler,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Fatura kesildi: ${data.fatura_no || ''}`);
        navigate('/satislar/faturalar');
      } else {
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('Fatura kesme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Header title="Yeni Satış Faturası" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: colors.textPrimary }}>Yeni Satış Faturası</h2>
          <button
            onClick={() => navigate('/satislar/faturalar')}
            style={{ padding: '10px 20px', background: colors.surface, color: colors.textPrimary, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}
          >
            ← Geri
          </button>
        </div>

        {/* Belge bilgileri */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '24px', boxShadow: shadows.md, marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 20px', color: styles.secondary }}>Belge Bilgileri</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            {/* Müşteri */}
            <div>
              <label style={labelStyle}>Müşteri *</label>
              <SearchableSelect
                options={cariler.map((c) => ({ value: String(c.id), label: c.unvan }))}
                value={cariId}
                onChange={handleCariChange}
                placeholder="Müşteri ara…"
              />
            </div>

            {/* Satan firma */}
            <div>
              <label style={labelStyle}>Satan Firma *</label>
              <select value={satanFirmaId} onChange={(e) => setSatanFirmaId(e.target.value)} style={inputStyle}>
                <option value="">Firma Seçiniz</option>
                {firmalar.filter((f) => f.aktif !== false).map((f) => (
                  <option key={f.id} value={String(f.id)}>{f.ad}</option>
                ))}
              </select>
            </div>

            {/* Tarih */}
            <div>
              <label style={labelStyle}>Fatura Tarihi *</label>
              <input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)} style={inputStyle} />
            </div>

            {/* Vade tarihi */}
            <div>
              <label style={labelStyle}>Vade Tarihi *</label>
              <input type="date" value={vadeTarihi} onChange={(e) => setVadeTarihi(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ marginTop: '15px' }}>
            <label style={labelStyle}>Notlar</label>
            <input
              type="text"
              value={notlar}
              onChange={(e) => setNotlar(e.target.value)}
              placeholder="Fatura notu (opsiyonel)…"
              style={inputStyle}
            />
          </div>
        </div>

        {/* İrsaliye seçimi */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '24px', boxShadow: shadows.md, marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 10px', color: styles.secondary }}>Faturalanacak İrsaliyeler</h3>

          {!cariId && (
            <div style={{ padding: '20px', textAlign: 'center', color: colors.textTertiary, fontSize: '14px' }}>
              Önce müşteri seçiniz.
            </div>
          )}

          {cariId && irsLoading && (
            <div style={{ padding: '20px', textAlign: 'center', color: colors.textSecondary, fontSize: '14px' }}>
              İrsaliyeler yükleniyor…
            </div>
          )}

          {cariId && !irsLoading && irsaliyeler.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: colors.textTertiary, fontSize: '14px' }}>
              Bu müşterinin faturalanabilir (sevk edilmiş, henüz faturalanmamış) irsaliyesi yok.
            </div>
          )}

          {cariId && !irsLoading && irsaliyeler.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                    <th style={{ padding: '10px 12px', width: '40px', textAlign: 'center' }}>Seç</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>İrsaliye No</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Tarih</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Tutar (Brüt)</th>
                  </tr>
                </thead>
                <tbody>
                  {irsaliyeler.map((ir) => (
                    <tr
                      key={ir.id}
                      onClick={() => toggleSecim(ir.id)}
                      style={{ borderBottom: `1px solid ${colors.border}`, cursor: 'pointer', background: secili[ir.id] ? colors.infoLight : 'white' }}
                    >
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!secili[ir.id]}
                          onChange={() => toggleSecim(ir.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`${ir.irsaliye_no} seç`}
                        />
                      </td>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px', color: '#1565c0' }}>{ir.irsaliye_no || '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{fmtTarih(ir.tarih)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                        {brutMap[ir.id] != null ? `${formatTutarTR(brutMap[ir.id])} TL` : '…'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Seçim özeti */}
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ minWidth: '300px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                    <span style={{ color: colors.textSecondary }}>Seçilen irsaliye:</span>
                    <span style={{ fontWeight: 'bold' }}>{seciliIdler.length}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: `1px solid ${colors.border}`, marginTop: '4px', fontWeight: 'bold', fontSize: '16px' }}>
                    <span>Toplam (Brüt):</span>
                    <span style={{ fontFamily: 'monospace', color: styles.primary }}>
                      {formatTutarTR(seciliBrutToplam)} TL{!toplamHesaplandi && seciliIdler.length > 0 ? ' …' : ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Aksiyonlar */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => navigate('/satislar/faturalar')}
            style={{ padding: '12px 24px', background: colors.surface, color: colors.textPrimary, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
          >
            İptal
          </button>
          <button
            onClick={handleKaydet}
            disabled={saving || !canManage || seciliIdler.length === 0}
            title={!canManage ? 'Bu işlem için yetkiniz yok' : (seciliIdler.length === 0 ? 'En az bir irsaliye seçiniz' : undefined)}
            style={{
              padding: '12px 32px', border: 'none', borderRadius: radius.sm,
              background: (saving || !canManage || seciliIdler.length === 0) ? '#90caf9' : styles.primary, color: 'white',
              cursor: (saving || !canManage || seciliIdler.length === 0) ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '14px',
            }}
          >
            {saving ? 'Kesiliyor…' : 'Fatura Kes'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default SatisFaturasiOlusturPage;
